import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { carrierUtils, flightsSessionStore, isFeatureRunningClientSide, pricingUtils } from "@flights/web-api-utils-universal";
import { isOfGoogleFlightsOrigin, isOfSkyScannerOrigin } from "../../utils/marketing-url-params.js";
import { actions as flightDetailsActions } from "../../store/flightDetails/actions.js";
import { actions as extraProductActions } from "../../ancillaries/store/extraProducts/actions.js";
import { trackMetaLandingsCustomGoalsV2 } from "../../utils/experiments/track-meta-landings-v2.js";
import { metaPreOptedAncillariesFromFlightToStore } from "../../utils/preOptedAncillaries.js";
import { FLIGHT_DETAILS_RETRYABLE_ERROR_CODES, FLIGHT_DETAILS_MODAL_CLOSE_ERROR_CODES, PRICE_ACCURACY_TAG_FIRED_OFFER } from "../../constants.js";
import { useGoogleFlightsPriceAccuracyTag, useSkyScannerPriceAccuracyTag } from "../../hooks/useGTMPriceAccuracyTags.js";
import { useAdTechOfferViewedTag } from "../../hooks/useAdTechGTMTags.js";
import useEventTracking from "../../hooks/useEventTracking.js";
import useRetryFlightDetailsApi from "../../hooks/useRetryFlightDetailsApi.js";
import useCreateUrl from "../../hooks/useCreateUrl.js";
import useEvents from "../../hooks/useEvents.js";
import useGlobalContext from "../../hooks/useGlobalContext.js";
import useClientMetrics from "../../hooks/useClientMetrics.js";
import useGetSearchResultsBound from "../../hooks/useGetSearchResultsBound.js";
import { useSplitBoundsCondition } from "../../utils/experiments/funnel/flights_split_bounds_us.js";
import { useSearchResultOffer } from "../../hooks/useSearchResults.js";
import { isDeltaAirlinesOffer } from "../../components/elements/FlightCard/utils.js";
import { useActions, useStore } from "../../store/index.js";
export function useFlightDetailsDesktopController(params, landingMetaOffer, locationSearch, flightSR) {
    const store = useStore();
    const actions = useActions(flightDetailsActions);
    const extrasActions = useActions(extraProductActions);
    const history = useHistory();
    const { createUrl } = useCreateUrl();
    const { grumble } = useEvents();
    const { requestId, features } = useGlobalContext();
    const { trackRenderTime, trackClientMetric } = useClientMetrics();
    const trackV2 = useEventTracking("flight_details", requestId);
    const googleFlightsPriceAccuracyTag = useGoogleFlightsPriceAccuracyTag();
    const skyScannerPriceAccuracyTag = useSkyScannerPriceAccuracyTag();
    const fireAdTechOfferViewedTag = useAdTechOfferViewedTag();
    const { setFlightDetailsApiForRetry } = useRetryFlightDetailsApi();
    const isGoogleFlightsUser = isOfGoogleFlightsOrigin();
    const isSkyscannerUser = isOfSkyScannerOrigin();
    const flightOffer = useSearchResultOffer(params?.id);
    const isDelta = isFeatureRunningClientSide("FLIGHTS_DELTA_DONT_SKIP_FETCH_BRANDED_FARES") &&
        flightOffer &&
        isDeltaAirlinesOffer(flightOffer);
    const requestableBrandedFares = flightOffer?.requestableBrandedFares;
    const shouldSkipBrandedFares = flightOffer && !isDelta && !requestableBrandedFares;
    const { flight: fetchedFlight, fetchStatus } = store.flightDetails;
    const flight = useMemo(() => {
        if (fetchStatus === "success" && fetchedFlight)
            return fetchedFlight;
        return flightSR;
    }, [fetchStatus, fetchedFlight, flightSR]);
    const enableGfsPosMismatch = features["FLIGHTS_META_ENABLE_GFS_FOR_POS_MISMATCH"] && isGoogleFlightsUser;
    const posMismatch = useMemo(() => {
        return flight?.posMismatch?.isPOSMismatch && !enableGfsPosMismatch ? flight.posMismatch : undefined;
    }, [flight, enableGfsPosMismatch]);
    const bound = useGetSearchResultsBound();
    const { isSplitBounds: shouldShowSplitBounds } = useSplitBoundsCondition();
    const backToSearch = useCallback((p) => {
        actions.fetch();
        const urlParams = new URLSearchParams(locationSearch);
        urlParams.delete("selectedProducts");
        urlParams.delete("flexibleTicket");
        urlParams.delete("cancelForAnyReason");
        urlParams.delete("brandedFareIndex");
        let url = `/flights/${params?.airports}?${urlParams.toString()}`;
        if (shouldShowSplitBounds) {
            if (bound === "outbound") {
                url = `flights/${params?.airports}/outbound/?${urlParams.toString()}`;
            }
            if (bound === "inbound") {
                url = `flights/${params?.airports}/inbound/${params?.outboundToken}/?${urlParams.toString()}`;
            }
        }
        history.push(createUrl(url), { forceRefetch: p?.forceReload });
    }, [actions, locationSearch, params?.airports, params?.outboundToken, shouldShowSplitBounds, history, createUrl, bound]);
    const onCloseWithTracking = useCallback((p) => {
        trackMetaLandingsCustomGoalsV2(1, "desktop");
        backToSearch({ forceReload: p?.forceReload });
    }, [backToSearch]);
    const handleErrorClose = useCallback(() => {
        const errorDoesNotNeedReload = FLIGHT_DETAILS_RETRYABLE_ERROR_CODES.includes(store.flightDetails.error?.code || "");
        onCloseWithTracking({ forceReload: !errorDoesNotNeedReload });
    }, [store.flightDetails.error, onCloseWithTracking]);
    const onFetchSuccess = useCallback((data) => {
        actions.fetchSuccess(data);
        data.ancillaries && metaPreOptedAncillariesFromFlightToStore(data.ancillaries, extrasActions);
        if (params?.id === landingMetaOffer && isGoogleFlightsUser) {
            trackV2("google_flights_landing_shallow", {
                isShallow: false,
                airlineIATA: carrierUtils.getUniqueCarrierCodes(data.segments)
            });
            trackClientMetric("google_flights_price_accuracy_tag_fired");
            googleFlightsPriceAccuracyTag(pricingUtils.priceToNumber(data.priceBreakdown.total), data.priceBreakdown.total.currencyCode);
        }
        if (params?.id === landingMetaOffer &&
            flightsSessionStore.get(PRICE_ACCURACY_TAG_FIRED_OFFER) !== params?.id &&
            isSkyscannerUser) {
            skyScannerPriceAccuracyTag(pricingUtils.priceToNumber(data.priceBreakdown.total), data.priceBreakdown.total.currencyCode);
            flightsSessionStore.set(PRICE_ACCURACY_TAG_FIRED_OFFER, params?.id);
        }
        void fireAdTechOfferViewedTag({
            offerReferenceId: data.offerReference || data.token,
            price: pricingUtils.priceToNumber(data.priceBreakdown.total),
            currency: data.priceBreakdown.total.currencyCode,
            availability: 1
        });
        const hasBrandedFares = data?.brandedFareOffers && data.brandedFareOffers.length > 0;
        if (shouldSkipBrandedFares && hasBrandedFares) {
            grumble("branded_fares_available_when_flag_false", {
                token: data.token,
                pointOfSale: data.pointOfSale,
                requestableBrandedFares: requestableBrandedFares,
                numOfBrandedFares: data?.brandedFareOffers && data.brandedFareOffers.length
            });
        }
    }, [
        actions,
        extrasActions,
        params?.id,
        landingMetaOffer,
        isGoogleFlightsUser,
        isSkyscannerUser,
        fireAdTechOfferViewedTag,
        shouldSkipBrandedFares,
        trackV2,
        trackClientMetric,
        googleFlightsPriceAccuracyTag,
        skyScannerPriceAccuracyTag,
        grumble,
        requestableBrandedFares
    ]);
    const onFetch = useCallback(() => {
        actions.fetch();
    }, [actions]);
    const onFetchError = useCallback((error) => {
        grumble("Failed to fetch flight details on desktop", error);
        if (FLIGHT_DETAILS_MODAL_CLOSE_ERROR_CODES.includes(error.code)) {
            onCloseWithTracking({ forceReload: false });
            return;
        }
        if (isGoogleFlightsUser) {
            trackV2("google_flights_landing_shallow", {
                isShallow: true,
                airlineIATA: []
            });
            trackClientMetric("google_flights_price_accuracy_tag_fired");
            trackClientMetric("google_flights_price_accuracy_tag_shallow");
            googleFlightsPriceAccuracyTag(0, "");
        }
        if (flight) {
            setFlightDetailsApiForRetry(error);
        }
        actions.fetchError(error);
    }, [
        flight,
        grumble,
        actions,
        trackV2,
        isGoogleFlightsUser,
        trackClientMetric,
        onCloseWithTracking,
        setFlightDetailsApiForRetry,
        googleFlightsPriceAccuracyTag
    ]);
    const onFetchAbort = useCallback(() => {
        actions.fetchReset();
    }, [actions]);
    return {
        flight,
        flightOffer,
        fetchStatus,
        onFetch,
        onFetchAbort,
        onFetchError,
        onFetchSuccess,
        posMismatch,
        backToSearch,
        onCloseWithTracking,
        handleErrorClose,
        trackRenderTime,
        store
    };
}
